import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { getContextualError, logError } from "../../utils/errorHandling";
import { validatePassword } from "../../utils/passwordValidation";
import { scrollToError } from "../../utils/scrollToError";
import PasswordRequirementsList from "./PasswordRequirementsList";
import authStyles from "./AuthForm.module.css";
import styles from "./PasswordChange.module.css";

interface PasswordChangeProps {
  onComplete?: () => void;
  required?: boolean;
}

/** Shape of the rejection thrown by `updatePassword` that this form inspects. */
interface PasswordChangeError {
  status?: number;
  message?: string;
}

const PasswordChange: React.FC<PasswordChangeProps> = ({
  onComplete,
  required = false,
}) => {
  const { updatePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showPasswords, setShowPasswords] = useState<boolean>(false);

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("All fields are required");
      setLoading(false);
      scrollToError(`.${authStyles["error-message"]}`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      setLoading(false);
      scrollToError(`.${authStyles["error-message"]}`);
      return;
    }

    if (currentPassword === newPassword) {
      setError("New password must be different from current password");
      setLoading(false);
      scrollToError(`.${authStyles["error-message"]}`);
      return;
    }

    // Validate password strength
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setError(`Password must contain: ${passwordErrors.join(", ")}`);
      setLoading(false);
      scrollToError(`.${authStyles["error-message"]}`);
      return;
    }

    try {
      await updatePassword(newPassword, currentPassword);

      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      logError(err, "passwordChange");

      const apiError = err as PasswordChangeError;
      if (apiError.status === 401) {
        setError(
          "Current password is incorrect. Please verify you are entering the correct temporary password.",
        );
      } else if (apiError.status === 422) {
        setError(apiError.message ?? "");
      } else {
        setError(
          getContextualError(err, undefined, "Failed to change password."),
        );
      }
      scrollToError(`.${authStyles["error-message"]}`);
    }

    setLoading(false);
  };

  const passwordStrength = validatePassword(newPassword);
  const isPasswordValid = passwordStrength.length === 0;

  return (
    <div className={styles["password-change-overlay"]}>
      <div className={styles["password-change-modal"]}>
        <div className={styles["password-change-header"]}>
          <h2>{required ? "Set Your New Password" : "Change Password"}</h2>
          <p>
            {required
              ? "For your security, please set a new password before continuing."
              : "Choose a strong password to keep your account secure."}
          </p>
        </div>

        {error && <div className={authStyles["error-message"]}>{error}</div>}

        <form
          onSubmit={handleSubmit}
          className={styles["password-change-form"]}
        >
          <div className={authStyles["form-group"]}>
            <label htmlFor="current-password">Current Password</label>
            <div className={styles["password-input"]}>
              <input
                id="current-password"
                type={showPasswords ? "text" : "password"}
                value={currentPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCurrentPassword(e.target.value)
                }
                placeholder="Enter your current password"
                required
                disabled={loading}
                autoComplete="current-password"
              />
            </div>
          </div>

          <div className={authStyles["form-group"]}>
            <label htmlFor="new-password">New Password</label>
            <div className={styles["password-input"]}>
              <input
                id="new-password"
                type={showPasswords ? "text" : "password"}
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewPassword(e.target.value)
                }
                placeholder="Enter your new password"
                required
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            {newPassword && <PasswordRequirementsList password={newPassword} />}
          </div>

          <div className={authStyles["form-group"]}>
            <label htmlFor="confirm-password">Confirm New Password</label>
            <div className={styles["password-input"]}>
              <input
                id="confirm-password"
                type={showPasswords ? "text" : "password"}
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConfirmPassword(e.target.value)
                }
                placeholder="Confirm your new password"
                required
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            {confirmPassword && newPassword !== confirmPassword && (
              <div className={authStyles["password-mismatch"]}>
                Passwords do not match
              </div>
            )}
          </div>

          <div className={authStyles["form-group"]}>
            <label className={authStyles["show-passwords-checkbox"]}>
              <input
                type="checkbox"
                checked={showPasswords}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setShowPasswords(e.target.checked)
                }
                disabled={loading}
              />
              Show passwords
            </label>
          </div>

          <div className={styles["password-change-actions"]}>
            <button
              type="submit"
              disabled={
                loading || !isPasswordValid || newPassword !== confirmPassword
              }
              className={styles["submit-btn"]}
            >
              {loading ? "Updating Password..." : "Update Password"}
            </button>

            {!required && (
              <button
                type="button"
                onClick={onComplete}
                disabled={loading}
                className={styles["cancel-btn"]}
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        {required && (
          <div className={styles["required-notice"]}>
            <p>
              <small>
                You must change your password to continue using the system.
              </small>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordChange;
